import { downloadSurah, isSurahDownloaded, wouldExceedBudget } from './offlineStore'
import { getSurahsSizeEstimate } from './audioSizes'

/**
 * Silent auto-download used by the "auto-download while playing" setting.
 * One surah at a time, at most one attempt per surah per session, budget
 * and connectivity respected, no dialogs (enabling the setting is the
 * user's standing consent). Failures are silent: the user is listening,
 * not managing downloads; a retry happens naturally next session.
 */

const attemptedThisSession = new Set<number>()
let downloadActive = false

export interface AutoDownloadOptions {
  enabled: boolean
  isOnline: boolean
  surahNumber: number
  reciter: string
  edition: string
  budgetMB: number
}

export async function maybeAutoDownload(opts: AutoDownloadOptions): Promise<'started' | 'skipped'> {
  const { enabled, isOnline, surahNumber, reciter, edition, budgetMB } = opts
  if (!enabled || !isOnline) return 'skipped'
  if (!Number.isInteger(surahNumber) || surahNumber < 1 || surahNumber > 114) return 'skipped'
  if (downloadActive || attemptedThisSession.has(surahNumber)) return 'skipped'
  if (isSurahDownloaded(surahNumber)) return 'skipped'

  // Set BEFORE the async estimate fetch, not after — otherwise two
  // overlapping invocations (e.g. React StrictMode's synchronous double-
  // invoke of an effect in dev; this app mounts under StrictMode) both read
  // `downloadActive` as false in the guard above, both await the estimate,
  // and both proceed to download. Setting it here — synchronously, before
  // the first `await` in this function — closes that TOCTOU window: the
  // check above and this set happen in the same synchronous turn, so a
  // second overlapping call sees it as `true` and is turned away. The
  // widened try/finally guarantees it's cleared on every exit from this
  // point on: the budget-skip return, a (contract-violating) throw from
  // the estimate fetch, or the normal post-download path.
  downloadActive = true
  try {
    const estimate = await getSurahsSizeEstimate(reciter, [surahNumber])
    if (estimate !== null && wouldExceedBudget(estimate, budgetMB)) return 'skipped'

    attemptedThisSession.add(surahNumber)
    try {
      await downloadSurah(surahNumber, { reciter, edition })
    } catch {
      // Silent by design; see module comment.
    }
    return 'started'
  } finally {
    downloadActive = false
  }
}

export function resetAutoDownloadStateForTests(): void {
  attemptedThisSession.clear()
  downloadActive = false
}
